import { useMemo, useState } from "react";
import { formatTime } from "../../js/utils/formatters";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export default function CalendarWidget({
  events = [],
  selectedDate,
  onSelectDate,
  compact = false,
  indicatorsOnly = false,
}) {
  const [cursor, setCursor] = useState(() => selectedDate || new Date());
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const start = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrev = new Date(year, month, 0).getDate();
    const list = [];
    for (let i = start - 1; i >= 0; i -= 1) {
      list.push({ day: daysInPrev - i, muted: true, date: new Date(year, month - 1, daysInPrev - i) });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      list.push({ day, muted: false, date: new Date(year, month, day) });
    }
    while (list.length % 7 !== 0) {
      const extra = list.length - (start + daysInMonth) + 1;
      list.push({ day: extra, muted: true, date: new Date(year, month + 1, extra) });
    }
    return list;
  }, [cursor, month, year]);

  const selectedKey = (selectedDate || new Date()).toISOString().slice(0, 10);
  const todayKey = new Date().toISOString().slice(0, 10);
  const eventDates = new Set(events.map((event) => event.date));
  const dayEvents = events.filter((event) => event.date === selectedKey);

  return (
    <div className={`calendar-widget${compact ? " calendar-widget--compact" : ""}${indicatorsOnly ? " calendar-widget--indicators" : ""}`}>
      <div className="calendar-header">
        <button type="button" className="ghost-btn" onClick={() => setCursor(new Date(year, month - 1, 1))}>
          ‹
        </button>
        <strong>
          {cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
        </strong>
        <button type="button" className="ghost-btn" onClick={() => setCursor(new Date(year, month + 1, 1))}>
          ›
        </button>
      </div>
      <div className="calendar-grid">
        {DAYS.map((day) => (
          <div key={day} className="calendar-dow">
            {day}
          </div>
        ))}
        {cells.map((cell, index) => {
          const key = cell.date.toISOString().slice(0, 10);
          const classes = [
            "calendar-day",
            cell.muted ? "is-muted" : "",
            key === todayKey ? "is-today" : "",
            !indicatorsOnly && key === selectedKey ? "is-selected" : "",
            eventDates.has(key) ? "has-event" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={`${key}-${index}`}
              type="button"
              className={classes}
              onClick={() => {
                if (indicatorsOnly) return;
                onSelectDate?.(cell.date);
              }}
              tabIndex={indicatorsOnly ? -1 : 0}
              aria-hidden={indicatorsOnly ? true : undefined}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
      <ul className="event-list">
        {dayEvents.length ? (
          dayEvents.map((event) => (
            <li key={event.id} className="event-item">
              <strong>{event.title}</strong>
              <div className="muted">
                {formatTime(event.time)} · {event.type} · {event.status}
              </div>
            </li>
          ))
        ) : (
          <li className="muted">No events marked this day.</li>
        )}
      </ul>
    </div>
  );
}
